import React from 'react'
import { CtxContext } from '../../Ctx'
import { wrapCallGlobalLifecycle } from './lifecycle'

const METHOD_NEEDLE_GET_CTX = '__AL_EX_needle_getCtx_RNM14e1eaq8YImpV'
const INTERNAL_SCOPE_STORE_KEY =
  '__AL_EX_INTERNAL_SCOPE_FOR_CTX_RNM14e1eaq8YImpV'

export default function withCtx(
  App,
  { isExportMode, globalConfig, serverRuntimeConfig, runtimeConfig }
) {
  return class extends React.PureComponent {
    static displayName = `fwAppWithCtx(${App.displayName || App.name || 'App'})`

    // SSR 页，在服务端执行，客户端不执行
    // CSR 时，首页不执行（直接使用SSR推导状态），其他页执行
    static async getInitialProps(appCtx) {
      // 初始化 internalScope
      let internalScope
      if (process.server) {
        internalScope = createInternalScope()
      } else {
        internalScope = window[INTERNAL_SCOPE_STORE_KEY]
      }

      // server 时，ctx 无需处理，总是系统框架提供实例
      // csr 首页时，不执行到此，由 constructor 处理
      // csr 非首页时，新建 ctx 对象
      let ctx = appCtx.ctx
      // 不能直接用 internalScope.lifecycleStatus.isFirstPage，此时还不准确
      // 从框架生命周期角度，只要执行到此，必然是 ssr 或 csr 的非首页
      if (process.browser) {
        // NextJs 的默认实现是，持续重用 ctx，实际不合理。 ctx 生命周期应与页面相同
        // 变更行为。新建新实例，不使用系统重用的实例
        ctx = makeMockCtx()
        appCtx.ctx = ctx
      }
      appendCtx(ctx, { router: appCtx.router, internalScope })

      // 完成 next 的 getInitialProps 生命周期
      let initialProps = {}
      // HACK 直接使用 ExtendedApp 执行会有报错，不确定原因，直接使用 App 绕过
      if ('getInitialProps' in App) {
        initialProps = await App.getInitialProps(appCtx)
      }

      return {
        ...initialProps,
        [METHOD_NEEDLE_GET_CTX]: () => ctx,
      }
    }

    constructor(props) {
      super(props)

      // 对于 ssr / csr routeChange 由 getInitialProps 产生 ctx 实例的场景，安全
      // 对于 csr 首页，必须在 constructor() 产生 ctx 实例，在 render() appendCtx
      // 以确保发生了异常的多次渲染，实例关系也正确
      // * 注意，不能以 function Component 的形式，在 render 处创建 ctx 实例
      // * 原因是，难以确保子级或业务，一定准确的处理了 ctx 实例变化时的行为
      // * 宽松假定为，同一页面上，ctx 实例一定不变，但 ctx 上的字段可能发生变化
      let ctx
      if (props[METHOD_NEEDLE_GET_CTX]) {
        ctx = props[METHOD_NEEDLE_GET_CTX]()
      } else {
        ctx = makeMockCtx()

        // 初始化 internal scope
        let internalScope
        if (process.browser) {
          if (!window[INTERNAL_SCOPE_STORE_KEY]) {
            internalScope = window[
              INTERNAL_SCOPE_STORE_KEY
            ] = createInternalScope()
          } else {
            internalScope = window[INTERNAL_SCOPE_STORE_KEY]
          }
        } else {
          throw new Error('框架时序异常')
        }

        appendCtx(ctx, { router: props.router, internalScope })
      }
      this.ctx = ctx
    }

    render() {
      const ctx = this.ctx
      // 额外的再次 appendCtx，保证 ctx 一定挂载了最新内容
      appendCtx(ctx, {
        router: this.props.router,
        internalScope: ctx.__AL_EX_internalScope,
      })

      return (
        <CtxContext.Provider value={ctx}>
          <App {...this.props} ctx={ctx} />
        </CtxContext.Provider>
      )
    }
  }

  // 对于无 ctx 的场景，按 next 定义模拟 ctx
  function makeMockCtx() {
    // 原样模拟，原始 ctx 的 key 为
    //    [ 'err', 'req', 'res', 'pathname', 'query', 'asPath', 'AppTree' ]
    return {
      // browser 无 err/req/res，保持与 next 形式一致，不提供 key
      // 此时可能还不准确，路由可能尚未变化完，appendCtx时设置
      pathname: undefined,
      query: undefined,
      asPath: undefined,
    }
  }

  function appendCtx(ctx, { router, internalScope }) {
    ctx.__AL_EX_internalScope = internalScope
    ctx.lifecycleStatus = internalScope.lifecycleStatus
    ctx.router = router

    // 此时，router 可能未变化完成，以 getter 方式提供，绕开时序问题
    Object.defineProperties(ctx, {
      pathname: {
        get() {
          return router.pathname
        },
      },
      query: {
        get() {
          return router.query
        },
      },
      asPath: {
        get() {
          return router.asPath
        },
      },
    })

    ctx.runtimeConfig = internalScope.runtimeConfig
    ctx.serverRuntimeConfig = internalScope.serverRuntimeConfig
  }

  function createInternalScope() {
    /*
     * 内部框架作用域，统一传递框架行为修改所需的数据
     */

    // 是否是首个页面，依赖 App 生命周期判定
    let isFirstPage = true
    const updateIsFirstPage = (value) => {
      isFirstPage = value
    }

    // 额外的初始状态，用于将渲染过程中 redux 等产生的状态，同步回 _document 最终同步回 client
    const extraInitialState = []

    // 生命周期状态
    const lifecycleStatus = {}
    const callGlobalLifecycle = wrapCallGlobalLifecycle(
      globalConfig,
      isExportMode,
      {
        lifecycleStatus,
        resolveIsFirstPage: () => isFirstPage,
      }
    )

    // 内部作用域，仅 framework 使用
    const internalScope = {
      '#': '危险，业务工程切勿使用，不满足Semver',
      globalConfig,
      runtimeConfig: runtimeConfig,
      serverRuntimeConfig: process.browser ? undefined : serverRuntimeConfig,
      updateIsFirstPage,
      lifecycleStatus,
      callGlobalLifecycle,
      extraDocProps: {}, // 额外需注入到 Document 上的 props
      extraDocPageProps: {}, // 额外需注入到 Document 上的 pageProps
      collectExtraInitialState(stateOrFunc) {
        extraInitialState.push(stateOrFunc)
      },
      computeExtraInitialState() {
        const result = {}
        extraInitialState.forEach((item) => {
          if (typeof item === 'function') {
            Object.assign(result, item())
          } else {
            Object.assign(result, item)
          }
        })
        return result
      },
      // 扩展生命周期
      initialModuleLifecycleProps: callGlobalLifecycle(undefined, {
        isModule: true,
        isApp: false,
        isPage: false,
        isInstance: false,
      }),
    }

    return internalScope
  }
}
